import axios from "axios";
import axiosInstance from "@/helpers/axiosInstance";
import { ApiResponse } from "@/types";

export interface NotificationSettings {
    id: string;
    type: string;
    enabled: boolean;
    userId: string;
}

export interface InAppUnreadCountResponse {
    count: number;
}

export type InAppNotificationType = "comment_reply" | "comment_like" | "new_follower" | "new_puzzle_followed";

export interface InAppPlayableProperty {
    id?: string;
    shortLink?: string | null;
    longLink?: string | null;
    property?: {
        id?: string | null;
        streetAddress?: string | null;
        city?: string | null;
        state?: string | null;
        zipCode?: string | null;
    } | null;
}

export interface InAppPlayablePropertyComment {
    id?: string;
    body?: string | null;
}

export interface InAppNotification {
    id: string;
    type: InAppNotificationType;
    userId: string;
    actorUserId: string;
    playablePropertyId: string | null;
    entityType: string | null;
    entityId: string | null;
    read: boolean;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string | null;
    actor?: Record<string, any>;
    recipient?: Record<string, any>;
    playableProperty?: InAppPlayableProperty;
    playablePropertyComment?: InAppPlayablePropertyComment;
}

/**
 * Optional query params for fetching a paginated list of in-app notifications.
 *
 * @property page - The 1-based page number to fetch.
 * @property limit - The maximum number of notifications to return per page.
 */
export interface GetInAppNotificationsParams {
    page?: number;
    limit?: number;
}

export interface InAppNotificationsResponse {
    data: InAppNotification[];
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    nextPage: number | null;
    previousPage: number | null;
}

/**
 * Retrieves the notification settings for a specific user.
 *
 * @param userId - The unique identifier of the user.
 * @param fetchAll - Whether to fetch all notification settings or just one.
 * @returns A Promise that resolves to an object representing the user's notification settings.
 */
export const getUserNotificationSettings = async (
    userId: string,
    fetchAll: boolean = false,
): Promise<ApiResponse<NotificationSettings[]>> => {
    return (
        await axios.get(`/api/notifications/user/${userId}`, {
            params: { fetchAll },
        })
    ).data;
};

export const getInAppNotifications = async (
    userId: string,
    params?: GetInAppNotificationsParams,
): Promise<InAppNotificationsResponse> => {
    const queryParams: GetInAppNotificationsParams = {
        page: 1,
        limit: 10,
        ...params,
    };

    const response = await axiosInstance.get<InAppNotificationsResponse>(
        `/api/notifications/in-app/${encodeURIComponent(userId)}`,
        {
            params: queryParams,
        },
    );

    return response.data;
};

export const getInAppUnreadNotificationCount = async (userId: string): Promise<InAppUnreadCountResponse> => {
    const response = await axiosInstance.get<InAppUnreadCountResponse>(
        `/api/notifications/in-app/${encodeURIComponent(userId)}/unread-count`,
    );
    return response.data;
};

/**
 * Marks a specific in-app notification as read for a user.
 *
 * @param userId - The unique identifier of the user.
 * @param notificationId - The unique identifier of the notification to mark as read.
 * @returns A Promise that resolves when the notification is successfully marked as read.
 */
export const markInAppNotificationAsRead = async (userId: string, notificationId: string): Promise<void> => {
    await axiosInstance.patch(
        `/api/notifications/in-app/${encodeURIComponent(userId)}/${encodeURIComponent(notificationId)}/read`,
    );
};

/**
 * Marks all in-app notifications as read for a user.
 *
 * @param userId - The unique identifier of the user.
 * @returns A Promise that resolves when all notifications are successfully marked as read.
 */
export const markAllInAppNotificationsAsRead = async (userId: string): Promise<void> => {
    await axiosInstance.patch(
        `/api/notifications/in-app/${encodeURIComponent(userId)}/read-all`,
    );
};
